import { ApiError } from '../../core/api-client.js';
import type { ClienteAutenticado } from '@pgcustom/shared-types';
import type { PortalClienteApi } from './portal-cliente.api.js';

/**
 * `<erp-portal-cliente-login-page>` — login do Portal do Cliente.
 * Mesmo padrão estrutural de `<erp-portal-parceiro-login-page>`, mas
 * deliberadamente um componente próprio, nunca o mesmo: fala com
 * `PortalClienteApi`/`/portal-clientes/login`, nunca com `AuthApi`/
 * `/auth/login` nem com `PortalParceiroApi` — os três contextos de
 * identidade são estruturalmente separados também no frontend (mesma
 * fronteira já aplicada no backend).
 */
export class ErpPortalClienteLoginPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _portalApi: PortalClienteApi | null = null;
  private _aoAutenticar: ((cliente: ClienteAutenticado) => void) | null = null;
  private enviando = false;
  private mensagemErro = '';
  private emailDigitado = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set portalApi(valor: PortalClienteApi) {
    this._portalApi = valor;
  }

  set aoAutenticar(valor: (cliente: ClienteAutenticado) => void) {
    this._aoAutenticar = valor;
  }

  connectedCallback(): void {
    this.render();
  }

  private async aoSubmeter(evento: Event): Promise<void> {
    evento.preventDefault();
    if (!this._portalApi || this.enviando) return;

    const email = this.root.querySelector<HTMLInputElement>('#email')?.value.trim() ?? '';
    const senha = this.root.querySelector<HTMLInputElement>('#senha')?.value ?? '';
    this.emailDigitado = email;

    if (!email || !senha) {
      this.mensagemErro = 'informe e-mail e senha';
      this.render();
      return;
    }

    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      const cliente = await this._portalApi.login(email, senha);
      this._aoAutenticar?.(cliente);
    } catch (erro) {
      this.mensagemErro =
        erro instanceof ApiError ? erro.message : 'não foi possível entrar. tente novamente.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private render(): void {
    this.root.innerHTML = `
      <style>
        :host {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          background: var(--pg-color-bg-base, #121417);
          font-family: var(--pg-font-familia-base, system-ui);
        }
        .cartao {
          width: 100%;
          max-width: 360px;
          padding: var(--pg-space-6, 32px);
        }
        .marca {
          font-family: var(--pg-font-familia-display, system-ui);
          font-size: var(--pg-font-size-xl, 1.5rem);
          font-weight: var(--pg-font-weight-bold, 700);
          color: var(--pg-color-texto-primario, #f2f3f5);
          margin-bottom: var(--pg-space-1, 4px);
        }
        .marca span { color: var(--pg-color-dourado, #c9a24b); }
        .subtitulo {
          font-size: var(--pg-font-size-sm, 0.875rem);
          color: var(--pg-color-texto-secundario, #9aa1ab);
          margin-bottom: var(--pg-space-6, 32px);
        }
        form { display: flex; flex-direction: column; gap: var(--pg-space-4, 16px); }
        label {
          display: flex;
          flex-direction: column;
          gap: var(--pg-space-2, 8px);
          font-size: var(--pg-font-size-sm, 0.875rem);
          color: var(--pg-color-texto-secundario, #9aa1ab);
        }
        input {
          background: var(--pg-color-bg-base, #121417);
          border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          border-radius: var(--pg-radius-md, 10px);
          color: var(--pg-color-texto-primario, #f2f3f5);
          padding: var(--pg-space-3, 12px);
          font-size: var(--pg-font-size-sm, 0.875rem);
        }
        input:focus-visible {
          outline: none;
          border-color: var(--pg-color-destaque, #2f8cff);
        }
        .erro {
          font-size: var(--pg-font-size-xs, 0.75rem);
          color: var(--pg-color-erro, #e5484d);
        }
        .erro:empty { display: none; }
        pg-button { width: 100%; }
        pg-button::part(button) { width: 100%; justify-content: center; }
      </style>
      <pg-card class="cartao">
        <div class="marca">PG <span>Custom</span></div>
        <div class="subtitulo">Portal do Cliente</div>
        <form novalidate>
          <label>
            E-mail
            <input id="email" type="email" autocomplete="username" required value="${escaparHtml(this.emailDigitado)}" />
          </label>
          <label>
            Senha
            <input id="senha" type="password" autocomplete="current-password" required />
          </label>
          <span class="erro">${escaparHtml(this.mensagemErro)}</span>
          <pg-button variant="primary" ${this.enviando ? 'loading' : ''}>Entrar</pg-button>
        </form>
      </pg-card>
    `;

    const form = this.root.querySelector('form');
    form?.addEventListener('submit', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('pg-button')?.addEventListener('pg-click', (evento) => this.aoSubmeter(evento));
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-portal-cliente-login-page', ErpPortalClienteLoginPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-portal-cliente-login-page': ErpPortalClienteLoginPage;
  }
}
